import { useMemo } from 'react';
import { useAuthStore } from '@features/authentication/store/authStore';
import { useCurrentRoleContext } from '@features/role-context/hooks/useRoleContext';
import { UserRole } from '@features/authentication/types';

/**
 * Hook to get the currently active role with priority system:
 * 1. PRIORITY 1: Role context (when user has switched roles)
 * 2. PRIORITY 2: Auth store roles (fallback for initial load)
 *
 * This ensures that role switching works correctly across all components
 * that check roles for permissions, navigation, etc.
 */
export const useActiveRole = () => {
  const { userSession, isAuthenticated } = useAuthStore();
  const { data: currentRoleContext } = useCurrentRoleContext();

  return useMemo(() => {
    if (!isAuthenticated) {
      return {
        currentRole: null,
        allRoles: [],
        hasRole: () => false,
        isSystemAdmin: false,
        isUniversityAdmin: false,
        isInstituteAdmin: false,
        isLecturer: false,
        isCourseRepresentative: false,
        source: 'none' as const,
      };
    }

    // PRIORITY 1: Use role context if available (role switching active)
    if (currentRoleContext?.roleType) {
      const activeRole = currentRoleContext.roleType;

      return {
        currentRole: activeRole,
        allRoles: [activeRole], // Only the active role matters when context is active
        hasRole: (role: UserRole | string) => {
          return activeRole === role || activeRole.toString() === role.toString();
        },
        isSystemAdmin: activeRole === UserRole.SYSTEM_ADMIN,
        isUniversityAdmin: activeRole === UserRole.UNIVERSITY_ADMIN,
        isInstituteAdmin: activeRole === UserRole.FACULTY_ADMIN,
        isLecturer: activeRole === UserRole.PROFESSOR,
        isCourseRepresentative: activeRole === UserRole.COURSE_REPRESENTATIVE,
        source: 'role-context' as const,
        contextData: currentRoleContext,
      };
    }

    // PRIORITY 2: Fallback to auth store roles (initial load, no role context)
    const authRoles = userSession?.currentRoles || [];

    if (authRoles.length === 0) {
      return {
        currentRole: null,
        allRoles: [],
        hasRole: () => false,
        isSystemAdmin: false,
        isUniversityAdmin: false,
        isInstituteAdmin: false,
        isLecturer: false,
        isCourseRepresentative: false,
        source: 'none' as const,
      };
    }

    // Use first role as primary (existing logic)
    const primaryRole = authRoles[0] as UserRole;

    return {
      currentRole: primaryRole,
      allRoles: authRoles,
      hasRole: (role: UserRole | string) => {
        return authRoles.some(
          (authRole) => authRole === role || authRole.toString() === role.toString()
        );
      },
      isSystemAdmin: authRoles.includes(UserRole.SYSTEM_ADMIN),
      isUniversityAdmin: authRoles.includes(UserRole.UNIVERSITY_ADMIN),
      isInstituteAdmin: authRoles.includes(UserRole.FACULTY_ADMIN),
      isLecturer: authRoles.includes(UserRole.PROFESSOR),
      isCourseRepresentative: authRoles.includes(UserRole.COURSE_REPRESENTATIVE),
      source: 'auth-store' as const,
    };
  }, [isAuthenticated, userSession?.currentRoles, currentRoleContext]);
};

/**
 * Simplified hook that just returns the active role type
 */
export const useCurrentActiveRole = (): UserRole | null => {
  const { currentRole } = useActiveRole();
  return currentRole;
};

/**
 * Hook to check if user has a specific role (role context aware)
 */
export const useHasRole = (role: UserRole | string): boolean => {
  const { hasRole } = useActiveRole();
  return hasRole(role);
};
